import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Profile } from '@/lib/types';
import { filterUsers, isAdminRole, roleCounts, type RoleFilter } from '@/application/users';
import { useRepositories } from '@/context/RepositoryContext';
import { useAuth } from '@/context/AuthContext';
import { useExpenses } from '@/hooks/useExpenses';
import { Icons } from '@/components/icons';
import { fmtDate } from '@/lib/format';

const AVATAR_COLORS = [
  'oklch(0.62 0.14 55)', 'oklch(0.55 0.10 150)', 'oklch(0.55 0.13 25)',
  'oklch(0.50 0.10 260)', 'oklch(0.52 0.11 200)', 'oklch(0.55 0.12 320)', 'oklch(0.50 0.09 100)',
];

function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initialsOf(name: string) {
  return (name || '').split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

function RoleBadge({ role }: { role: string }) {
  const admin = isAdminRole(role);
  return (
    <span className={`role-badge ${admin ? 'admin' : 'member'}`}>
      {admin && <Icons.Shield size={11} sw={1.8} />}
      {admin ? 'Admin' : 'Member'}
    </span>
  );
}

function RoleMenu({ profile, isSelf, busy, onSetRole }: { profile: Profile; isSelf: boolean; busy: boolean; onSetRole: (p: Profile, role: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const admin = isAdminRole(profile.role);

  return (
    <div className="row-menu-wrap" ref={ref}>
      <button className="btn icon ghost sm" onClick={() => setOpen((o) => !o)} title="Manage role" disabled={busy}>
        <Icons.More size={15} />
      </button>
      {open && (
        <div className="menu">
          <div className="mlabel">Role</div>
          {!admin && (
            <button onClick={() => { onSetRole(profile, 'admin'); setOpen(false); }}>
              <Icons.Shield size={15} /> Promote to admin
            </button>
          )}
          {admin && (
            <button
              onClick={() => { if (!isSelf) { onSetRole(profile, 'user'); setOpen(false); } }}
              disabled={isSelf}
              style={isSelf ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              <Icons.Users size={15} /> Change to member
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function UsersRoute() {
  const { profileRepository } = useRepositories();
  const { user } = useAuth();
  const { expenses } = useExpenses();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    profileRepository.listProfiles().then((data) => {
      setProfiles(data);
      setLoading(false);
    });
  }, [profileRepository]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => roleCounts(profiles), [profiles]);
  const filtered = useMemo(() => filterUsers(profiles, roleFilter, search), [profiles, roleFilter, search]);
  const receiptsByUser = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of expenses) map.set(e.user_id, (map.get(e.user_id) || 0) + 1);
    return map;
  }, [expenses]);

  const setRole = async (profile: Profile, role: string) => {
    setBusy(true);
    await profileRepository.updateRole(profile.id, role);
    load();
    setBusy(false);
  };

  if (loading) return <div className="empty"><div className="ttl">Loading team…</div></div>;

  return (
    <>
      <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="card metric">
          <div className="lbl">Team members</div>
          <div className="val">{counts.all}</div>
          <div className="delta">All accounts</div>
        </div>
        <div className="card metric">
          <div className="lbl">Admins</div>
          <div className="val" style={{ color: 'oklch(0.45 0.12 50)' }}>{counts.admin}</div>
          <div className="delta">Full access</div>
        </div>
        <div className="card metric">
          <div className="lbl">Members</div>
          <div className="val">{counts.member}</div>
          <div className="delta">Add &amp; view expenses</div>
        </div>
      </div>

      <div className="filters">
        {([
          { id: 'all', label: 'All', n: counts.all },
          { id: 'admin', label: 'Admins', n: counts.admin },
          { id: 'member', label: 'Members', n: counts.member },
        ] as { id: RoleFilter; label: string; n: number }[]).map((f) => (
          <button key={f.id} className={`filter-pill ${roleFilter === f.id ? 'active' : ''}`} onClick={() => setRoleFilter(f.id)}>
            {f.label} <span className="ct">{f.n}</span>
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input
          className="small"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', fontSize: 12.5, outline: 0, width: 220 }}
        />
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{filtered.length} {filtered.length === 1 ? 'account' : 'accounts'}</h3>
          <span className="sub">Manage roles &amp; access</span>
        </div>
        <div className="card-b tight">
          {filtered.length === 0 ? (
            <div className="empty">
              <Icons.Users size={26} stroke="var(--faint)" />
              <div className="ttl">No matching members</div>
              <div>Try a different filter or search.</div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th className="num">Receipts</th>
                  <th>Joined</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const name = p.display_name || p.email || 'Unknown';
                  const isSelf = p.id === user?.uid;
                  return (
                    <tr key={p.id} style={{ cursor: 'default' }}>
                      <td>
                        <div className="user-cell">
                          <span className="av" style={{ background: avatarColor(name) }}>{initialsOf(name)}</span>
                          <span className="nm">{name}</span>
                          {isSelf && <span className="you">You</span>}
                        </div>
                      </td>
                      <td><span className="mono-email">{p.email || '—'}</span></td>
                      <td><RoleBadge role={p.role} /></td>
                      <td className="num dt">{receiptsByUser.get(p.id) || 0}</td>
                      <td className="dt">{fmtDate(p.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <RoleMenu profile={p} isSelf={isSelf} busy={busy} onSetRole={setRole} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
